import type { ProbeResult } from '@vxh/shared';

/** A test result is kept for ten minutes per address, for this browser tab only (it is about where this person is right now). */
const KEEP_MS = 10 * 60_000;
const TIMEOUT_MS = 2000;
const key = (url: string) => `vxh-probe:${url}`;

export function cachedProbe(url: string): ProbeResult | undefined {
  try {
    const v = JSON.parse(sessionStorage.getItem(key(url)) ?? 'null') as { r: ProbeResult; at: number } | null;
    return v && Date.now() - v.at < KEEP_MS ? v.r : undefined;
  } catch {
    return undefined; // storage can be blocked: then every Open tests again
  }
}

function remember(url: string, r: ProbeResult) {
  try {
    sessionStorage.setItem(key(url), JSON.stringify({ r, at: Date.now() }));
  } catch {
    /* not kept */
  }
}

/**
 * Can THIS browser reach the instance's own address? The browser makes the request itself, so the answer is true to the person's network. `no-cors` means the
 * page cannot read the answer, only learn that one came back (an opaque response): any answer at all, even "not found", proves the address is reachable.
 * Nothing at all (no route, a certificate the browser does not trust, a refused connection, no answer in two seconds) means it is not.
 */
export async function probeDirect(url: string): Promise<ProbeResult> {
  const ctl = new AbortController();
  const timer = setTimeout(() => ctl.abort(), TIMEOUT_MS);
  try {
    await fetch(url, { mode: 'no-cors', cache: 'no-store', credentials: 'omit', referrerPolicy: 'no-referrer', signal: ctl.signal });
    remember(url, 'reachable');
    return 'reachable';
  } catch {
    remember(url, 'unreachable');
    return 'unreachable';
  } finally {
    clearTimeout(timer);
  }
}
